/*
 * Presentación del incremento 5/6. TODO lo que aquí se escribe es presentación:
 * ninguna función de este archivo decide una regla de negocio.
 *
 * EL PRESUPUESTO ES SOLO EN PESOS (D-112, RN-193): `importe` no recibe divisa
 * porque no hay nada que elegir. El gasto en dólares se informa aparte, con
 * `importeEnDivisa`, SIN CONVERTIR.
 *
 * El formato lo resuelve la implementación única del núcleo (RN-147d): aquí no se
 * vuelve a formatear a mano, y `US$` está derogado (D-88, RN-147b).
 */

import { formatearImporte } from '../../nucleo/importes/formatoDeImporte.js';

export const DIVISA_DEL_PRESUPUESTO = 'MXN';

const NOMBRES_DE_MES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/** Importe del presupuesto: siempre MXN, siempre "$1,234.56 MXN". */
export function importe(valor) {
  return formatearImporte(valor, DIVISA_DEL_PRESUPUESTO);
}

/**
 * Importe en OTRA divisa, para el bloque informativo de dólares. Se muestra en su
 * propia divisa y no se convierte: no hay tipo de cambio en el sistema (D-03).
 */
export function importeEnDivisa(valor, codigoISO) {
  return formatearImporte(valor, codigoISO);
}

/** Verdadero si una cadena decimal del servidor es negativa. Se lee el TEXTO, no un float. */
export function esNegativo(valorDecimal) {
  return /^\s*[-−]/.test(String(valorDecimal ?? ''));
}

/**
 * Porcentaje anual CON SIGNO EXPLÍCITO (D-134, RN-221d): "+10 %", "−10 %", "0 %".
 * Nulo se presenta vacío, nunca como "0 %": no es lo mismo que nadie lo haya
 * fijado a que lo hayan fijado en cero (D-144, RN-223b).
 */
export function porcentaje(valor) {
  if (valor === null || valor === undefined || valor === '') return '';
  const numero = Number(valor);
  if (Number.isNaN(numero)) return String(valor);
  const absoluto = Math.abs(numero);
  const texto = Number.isInteger(absoluto) ? absoluto.toFixed(0) : absoluto.toFixed(2);
  const signo = numero > 0 ? '+' : (numero < 0 ? '−' : '');
  return `${signo}${texto} %`;
}

/** Avance de una línea o del mes. Llega ya redondeado del servidor; aquí solo se rotula. */
export function avance(valor) {
  if (valor === null || valor === undefined) return '—';
  return `${Number(valor).toFixed(2)} %`;
}

export function anioDePeriodo(idPeriodo) {
  return Math.floor(Number(idPeriodo) / 100);
}

export function mesDePeriodo(idPeriodo) {
  return Number(idPeriodo) % 100;
}

/** "septiembre de 2026". */
export function nombreDePeriodo(idPeriodo) {
  const mes = mesDePeriodo(idPeriodo);
  if (!mes || mes < 1 || mes > 12) return String(idPeriodo);
  return `${NOMBRES_DE_MES[mes - 1]} de ${anioDePeriodo(idPeriodo)}`;
}

/** "SEPTIEMBRE 2026", para el encabezado del selector de mes. */
export function etiquetaDePeriodo(idPeriodo) {
  const mes = mesDePeriodo(idPeriodo);
  if (!mes || mes < 1 || mes > 12) return String(idPeriodo);
  return `${NOMBRES_DE_MES[mes - 1].toUpperCase()} ${anioDePeriodo(idPeriodo)}`;
}

export function mesAnterior(idPeriodo) {
  const anio = anioDePeriodo(idPeriodo);
  const mes = mesDePeriodo(idPeriodo);
  return mes === 1 ? (anio - 1) * 100 + 12 : anio * 100 + (mes - 1);
}

export function mesSiguiente(idPeriodo) {
  const anio = anioDePeriodo(idPeriodo);
  const mes = mesDePeriodo(idPeriodo);
  return mes === 12 ? (anio + 1) * 100 + 1 : anio * 100 + (mes + 1);
}

/** Periodo AAAAMM de una fecha de negocio AAAA-MM-DD del dispositivo (D-06). */
export function periodoDeFechaLocal(fechaLocal) {
  return Number(`${fechaLocal.slice(0, 4)}${fechaLocal.slice(5, 7)}`);
}

/** Lista de meses en palabras: "enero, marzo y julio". */
export function listaDeMeses(periodos) {
  const nombres = [...new Set((periodos ?? []).map((p) => mesDePeriodo(p)))]
    .sort((a, b) => a - b)
    .map((mes) => NOMBRES_DE_MES[mes - 1]);

  if (nombres.length === 0) return 'ningún mes';
  if (nombres.length === 1) return nombres[0];
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/**
 * Titular del aviso de la generación anual, CON EL NÚMERO QUE MANDA EL SERVIDOR
 * (D-167, RN-233 b ter 1 y b ter 2).
 *
 * LA UNIDAD ES LA CATEGORÍA DISTINTA, no la categoría-mes ni la línea: el
 * servidor entrega `cantidadDeCategoriasSinPresupuesto` ya resuelto y POR
 * ALCANCE, y la pantalla LO PINTA TAL CUAL. Cruzar `lineasManualesEnRiesgo` con
 * `detalleDeReemplazos` aquí sería exactamente el cálculo que RNF-053 prohíbe al
 * cliente.
 */
export function avisoDeCategoriasSinPresupuesto(cantidad) {
  const numero = Number(cantidad ?? 0);
  return numero === 1
    ? '1 categoría quedará sin presupuesto'
    : `${numero} categorías quedarán sin presupuesto`;
}

/* ===========================================================================
 * LOS AÑOS QUE SE PUEDEN PRESUPUESTAR (incremento 22/22; RF-275, RN-769)
 *
 * EL SERVIDOR ES QUIEN MANDA, y esto no lo sustituye: el año se valida contra el
 * calendario sembrado («el año existe entero», RN-769) y un año fuera responde con
 * el mensaje de siempre, «Ese año está fuera del rango disponible.». Lo de abajo
 * existe para DIBUJAR la cuadrícula: ofrecer años que el servidor rechazaría sería
 * mandar a la persona contra una pared.
 *
 * EL PISO NO SE MUEVE (RN-761, D-447) y el techo se DERIVA del año en curso —el de
 * la fecha de negocio del dispositivo, que es quien resuelve «hoy» (D-06)— con el
 * mismo horizonte que el servidor (RN-760). Es el ÚNICO lugar del frontend donde
 * vive este cálculo.
 * ======================================================================== */

/** RN-761 · el primer año presupuestable. Fijo. */
export const ANIO_PISO = 2025;

/** RN-760 · el horizonte de la regla derivada del techo. */
export const ANIOS_DE_HORIZONTE = 35;

/** RN-760 · último año presupuestable con ese año en curso. */
export function anioTecho(anioEnCurso) {
  return Number(anioEnCurso) + ANIOS_DE_HORIZONTE;
}

/**
 * Los años CERCANOS de la cuadrícula corta (decisión de Deisy Paez del
 * 2026-10-15, opción (b) del prototipo aprobado v45): el pasado, el de hoy y los
 * cuatro que vienen. El año elegido se queda a la vista aunque sea lejano, para no
 * tener que buscarlo otra vez al cerrar la lista completa.
 */
export function aniosCercanos(anioEnCurso, anioElegido) {
  const techo = anioTecho(anioEnCurso);
  const cercanos = [];

  for (let anio = Number(anioEnCurso) - 1; anio <= Number(anioEnCurso) + 4; anio += 1) {
    if (anio >= ANIO_PISO && anio <= techo) cercanos.push(anio);
  }

  const elegido = Number(anioElegido);
  if (elegido >= ANIO_PISO && elegido <= techo && !cercanos.includes(elegido)) {
    cercanos.push(elegido);
    cercanos.sort((uno, otro) => uno - otro);
  }

  return cercanos;
}

/** Todos los años presupuestables, del piso fijo al techo derivado. */
export function todosLosAnios(anioEnCurso) {
  const todos = [];
  for (let anio = ANIO_PISO; anio <= anioTecho(anioEnCurso); anio += 1) todos.push(anio);
  return todos;
}
